import React, { useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';
import { StartPageHeroBanner } from './start-page-hero-banner';
import { useIsFeatureEnabled } from 'tradera/hooks/use-is-feature-enabled';
import useIsMobile from 'tradera/hooks/use-is-mobile';
import { getImageSizes, getWebpImage } from '../image-sizes';
import { HEADER } from '../content-types';
import { trackContainerClick } from '../analytics';
import { isSpaLink, toRelativeTraderaUrl } from 'tradera/utils/url';
import ALink from 'tradera/components/alink/alink';
import MediaItem from '../media/media-item';
import { Search } from 'tradera/legacy/static/components/search/search';
import {
    toggleHeaderSearch as toggleHeaderSearchAction,
    openSearchForm
} from 'tradera/state/ui/reducer';
import RecentSearchesTags from '../recent-searches/recent-searches-tags';
import { StartPageHeroTrustmark } from './start-page-hero-trustmark';
import TrendingSearchesTags from '../trending/trending-searches-tags';
import { isServer } from 'tradera/utils/nextjs';
import useIsLoggedIn from 'tradera/hooks/use-is-loggedin';
import { useAppDispatch, useAppSelector } from 'tradera/state/hooks';
import { Heading } from 'tradera/components/atoms/heading/heading';

import styles from './start-page-hero.module.scss';

type StartPageHeroProps = {
    isLoading: boolean;
    showRecentSearches: boolean;
    showTrendingSearches: boolean;
};

export const StartPageHero = ({
    isLoading,
    showRecentSearches,
    showTrendingSearches
}: StartPageHeroProps) => {
    const {
        backgroundVideo,
        backgroundImage,
        isCampaignMode,
        trackingId,
        webpageUrl = '',
        taglineDesktop,
        taglineMobile
    } = useAppSelector((state) => state.views.startPage.config.header);
    const isLoggedIn = useIsLoggedIn();

    const videoEnabled = useIsFeatureEnabled('start-page-contentful-video');
    const video = videoEnabled ? backgroundVideo : null;

    const [searchElementRef, searchInView, searchInViewEntry] = useInView({
        rootMargin: '-45px 0px 0px 0px',
        threshold: 1.0
    });

    const dispatch = useAppDispatch();
    const toggleHeaderSearch = useCallback(
        (show: boolean) => dispatch(toggleHeaderSearchAction(show)),
        [dispatch]
    );
    useEffect(() => {
        if (searchInViewEntry) {
            const siteHeaderSearchInput = querySelectSearchInput();
            if (
                siteHeaderSearchInput &&
                siteHeaderSearchInput === document.activeElement
            ) {
                toggleHeaderSearch(true);
                return;
            }

            toggleHeaderSearch(!searchInView);
        } else if (isCampaignMode) {
            toggleHeaderSearch(true);
        }
    }, [searchInViewEntry, isCampaignMode, searchInView, toggleHeaderSearch]);

    const isMobile = useIsMobile();
    const imageSizes = getImageSizes(1, backgroundImage, HEADER);
    const webpImage = getWebpImage(1, backgroundImage, HEADER);
    const parsedCampaignUrl = isCampaignMode
        ? toRelativeTraderaUrl(webpageUrl)
        : null;

    const handleFocusOverride = () => {
        toggleHeaderSearch(true);
        dispatch(openSearchForm());

        /**
         * This is a temporary fix for the search input not being focused
         * when the search form is opened on mobile.
         * The issue is that we need to set the focus immidiately after a user input
         * and not wait for the next render cycle.
         */
        document.querySelector('#slim-header__search')?.classList.add('d-flex');
        document
            .querySelector('#site-header')
            ?.classList.remove('hide-elements');

        const siteHeaderSearchInput = querySelectSearchInput();
        siteHeaderSearchInput?.focus();
    };

    const querySelectSearchInput = () => {
        return document.querySelector(
            '#slim-header__search > form input[data-search-field]'
        ) as HTMLInputElement | null;
    };

    return (
        <>
            <div
                className={
                    isLoggedIn
                        ? styles.startPageHeroLoggedIn
                        : styles.startPageHero
                }
                data-test-startpage-hero>
                {backgroundImage ? (
                    <MediaItem
                        alternativeText="Start page cover image"
                        imageSizes={imageSizes}
                        image={backgroundImage}
                        video={video}
                        webpImage={webpImage}
                    />
                ) : (
                    <FallbackImage />
                )}
                {isCampaignMode && parsedCampaignUrl ? (
                    <ALink
                        useLink={isSpaLink(parsedCampaignUrl)}
                        href={parsedCampaignUrl}
                        onClick={() => trackContainerClick(HEADER, trackingId)}
                        style={{ top: '0px' }}
                        className="position-absolute w-100 h-100"
                        aria-label={trackingId}
                    />
                ) : (
                    <div
                        className={classNames(
                            styles.content,
                            styles.startPageHeroContent
                        )}>
                        <div
                            className="container h-100 d-flex flex-column justify-content-center"
                            style={{
                                opacity: isLoading ? 0 : 1,
                                transition: 'opacity 200ms linear'
                            }}>
                            {!isLoggedIn && (
                                <div className="text-center">
                                    {taglineDesktop && (
                                        <Heading
                                            branded
                                            size="madrid"
                                            className={classNames(
                                                'd-none d-md-block text-white mb-3',
                                                styles.taglineDesktop
                                            )}>
                                            {taglineDesktop}
                                        </Heading>
                                    )}

                                    {taglineMobile && (
                                        <Heading
                                            size="london"
                                            branded
                                            className={classNames(
                                                'd-md-none text-white mb-1 mb-sm-2',
                                                styles.taglineMobile
                                            )}>
                                            {taglineMobile}
                                        </Heading>
                                    )}
                                </div>
                            )}
                            <div className="row d-md-flex mb-2">
                                <div
                                    id="start-page-hero-search"
                                    className="col-12 col-sm-10 offset-sm-1 col-md-8 offset-md-2"
                                    ref={searchElementRef}>
                                    <Search
                                        inSPA={true}
                                        focusOverride={
                                            isMobile &&
                                            !isServer &&
                                            handleFocusOverride
                                        }
                                        inputClassName={styles.searchInputSize}
                                    />
                                </div>
                            </div>
                            {showRecentSearches && (
                                <div className="row d-flex mb-1">
                                    <div className="col-8 offset-2">
                                        <RecentSearchesTags inHeader />
                                    </div>
                                </div>
                            )}
                            {showTrendingSearches && (
                                <div className="row d-flex mb-1">
                                    <div className="col-8 offset-2">
                                        <TrendingSearchesTags inHeader />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <StartPageHeroTrustmark />
            </div>
            <StartPageHeroBanner />
        </>
    );
};

const FallbackImage = () => (
    <img
        className={styles.fallbackImage}
        srcSet="/static/images/start-page/header-fallback-large.jpg 1920w, /static/images/start-page/header-fallback-medium.jpg 768w, /static/images/start-page/header-fallback-small.jpg 425w"
        sizes="(max-width: 425px) 425px, (max-width: 768px) 768px, 1920px"
        src="/static/images/start-page/header-fallback-large.jpg"
        alt="Start page cover"
    />
);
